import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useLocation } from 'react-router-dom';
import TiffViewer from 'components/allocation-manager/allocation-tracts-table/components/allocation-manager-tiff-viewer';
import { isTiffFileName } from 'components/allocation-manager/allocation-tracts-table/utils';
import { InlineLoading } from '@carbon/react';
import styles from './page.module.scss';

interface DocumentInfo {
  attachmentHref: string | null;
  fileName: string | null;
}

export function DocumentViewerPage() {
  const { wellId, documentId } = useParams<{
    wellId: string;
    documentId: string;
  }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryUrl = queryParams.get('url');
  const queryFileName = queryParams.get('fileName') ?? 'document';

  const [documentInfo, setDocumentInfo] = useState<DocumentInfo | null>(null);
  const [loading, setLoading] = useState(!!(wellId && documentId));
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(true);

  useEffect(() => {
    if (!wellId || !documentId) return;

    setLoading(true);
    setError(null);
    fetch(`/wells/${wellId}/supporting_files/${documentId}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((res) => {
        if (!res.ok) throw new Error('Document not found');
        return res.json();
      })
      .then((data: DocumentInfo) => {
        setDocumentInfo(data);
      })
      .catch(() => setError('Document not found or not accessible.'))
      .finally(() => setLoading(false));
  }, [wellId, documentId]);

  const url =
    wellId && documentId
      ? (documentInfo?.attachmentHref ?? undefined)
      : queryUrl;
  const fileName =
    wellId && documentId
      ? (documentInfo?.fileName ?? 'document')
      : queryFileName;

  const isTiff = isTiffFileName(fileName);
  const showPdfViewer = url && !isTiff;

  useEffect(() => {
    if (showPdfViewer) setPdfLoading(true);
  }, [url, showPdfViewer]);

  const renderContent = () => {
    if (wellId && documentId) {
      if (loading) {
        return (
          <div className={styles.fullScreen}>
            <div className={styles.loadingState}>
              <div className={styles.loadingStateCenter}>
                <InlineLoading
                  description="Loading document..."
                  status="active"
                />
              </div>
            </div>
          </div>
        );
      }
      if (error || !documentInfo?.attachmentHref) {
        return (
          <div className={styles.fullScreen}>
            <div className={styles.errorState}>
              <p className={styles.errorTitle}>
                {error ?? 'Missing document URL.'}
              </p>
            </div>
          </div>
        );
      }
    } else if (!queryUrl) {
      return (
        <div className={styles.fullScreen}>
          <div className={styles.errorState}>
            <p className={styles.errorTitle}>Missing document URL.</p>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.fullScreen}>
        {isTiff ? (
          <div className={styles.tiffViewerWrapper}>
            <TiffViewer documentUrl={url!} fileName={fileName} fullPage />
          </div>
        ) : (
          <div className={styles.documentBody}>
            {pdfLoading && (
              <div className={styles.documentLoadingOverlay}>
                <div className={styles.documentLoadingOverlayCenter}>
                  <InlineLoading
                    description="Loading document..."
                    status="active"
                  />
                </div>
              </div>
            )}
            <iframe
              src={url!}
              title={fileName}
              className={styles.documentIframe}
              onLoad={() => setPdfLoading(false)}
            />
          </div>
        )}
      </div>
    );
  };

  return createPortal(renderContent(), document.body);
}
